import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import {
  useGetGigClaim,
  useGetGigClaims,
  useGetMyAccount,
  useGetMyProfile,
  useGetSetlists,
} from "@/api/queries";
import { hasAdminRights } from "@/auth/shared";
import countries from "@/data/countries";
import useAppStore from "@/store/appStateZustand";

import type { SectionKey } from "../types/gigs.types";
import type { GigClaimFormValues } from "../utils/gigsClaimSchema";

import { createGigsClaimSchema } from "../utils/gigsClaimSchema";
import { FLAGS } from "../utils/gigsClaimUtils";

export default function useGigsClaimForm() {
  const [showManual, setShowManual] = useState<boolean>(false);
  const [sections, setSections] = useState<SectionKey[]>([]);
  const [festival, setFestival] = useState<boolean>(false);
  const [otherPerformers, setOtherPerformers] = useState<boolean>(false);
  const [isUsAlertOpen, setIsUsAlertOpen] = useState<boolean>(false);
  const [isDenmarkAlertOpen, setIsDenmarkAlertOpen] = useState<boolean>(false);

  const previousCountryRef = useRef<string | undefined>(undefined);

  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const { id } = useParams({
    strict: false,
    select: (p): { id: string | undefined } => ({ id: (p as Record<string, string | undefined>).id }),
  });

  // Queries
  const myAccount = useGetMyAccount({ artistaccountcode: selectedAccount });
  const setlists = useGetSetlists({
    ac: selectedAccount,
    oc: myAccount?.data?.originatorcode?.code,
  });
  const profile = useGetMyProfile();
  const gigClaim = useGetGigClaim({ ac: selectedAccount, id });
  const gigClaims = useGetGigClaims({ ac: selectedAccount, oc: myAccount?.data?.originatorcode?.code });
  const { refetch: refetchGigClaim } = gigClaim;

  const isUpdate = Boolean(id);
  const adminRights = hasAdminRights(myAccount?.data);

  const schemaRef = useRef(createGigsClaimSchema([], false, false, false, isUpdate));

  const form = useForm<GigClaimFormValues>({
    resolver: async (values, context, options) => zodResolver(schemaRef.current)(values, context, options),
  });

  const watchedCountry = form.watch("country");
  const watchedAdmissionEUR = form.watch("admissionEUR");
  const watchedVenueCategoryId = form.watch("venuePremisesCategoryId");

  const underTwelveEuros = watchedCountry === "ES"
    && watchedAdmissionEUR != null
    && watchedAdmissionEUR > 0
    && watchedAdmissionEUR <= 11.99;

  const schema = useMemo(
    () => createGigsClaimSchema(sections, festival, otherPerformers, underTwelveEuros, isUpdate),
    [sections, festival, otherPerformers, underTwelveEuros, isUpdate],
  );

  useEffect(() => {
    schemaRef.current = schema;
    if (Object.keys(form.formState.touchedFields).length > 0) {
      void form.trigger();
    }
  }, [schema, form]);

  // Refetch on account change when editing
  useEffect(() => {
    if (selectedAccount && isUpdate) {
      void refetchGigClaim();
    }
  }, [selectedAccount, refetchGigClaim, isUpdate]);

  useEffect(() => {
    if (gigClaim.data && id != null) {
      const raw = gigClaim.data[0];
      const countrySelected = countries.find(e => e.value === raw.countryiso);
      const values: Partial<GigClaimFormValues> = {
        country: countrySelected?.value ?? raw.countryiso ?? undefined,
        venueName: raw.venueName ?? undefined,
        performer: raw.performer ?? undefined,
        city: raw.city ?? undefined,
        address: raw.address ?? undefined,
        postcode: raw.postcode ?? undefined,
        date: raw.date ?? undefined,
        time: raw.time ?? undefined,
        setlist: raw.setlist != null ? String(raw.setlist) : undefined,
        venueId: raw.venueId ?? undefined,
        venuePremisesCategoryId: raw.venuePremisesCategoryId ?? undefined,
        venuePremisesTypeId: raw.venuePremisesTypeId ?? undefined,
        prsId: raw.prsId != null ? String(raw.prsId) : undefined,
        venueIsApproved: raw.venueIsApproved != null ? Boolean(raw.venueIsApproved) : undefined,
        admissionFee: raw.admissionFee != null ? String(raw.admissionFee) : undefined,
        admissionEUR: raw.admissionEUR != null ? Number(raw.admissionEUR) : undefined,
        admissionCAD: raw.admissionCAD != null ? Number(raw.admissionCAD) : undefined,
        admissionDKK: raw.admissionDKK != null ? Number(raw.admissionDKK) : undefined,
        audienceNumber: raw.audienceNumber != null ? Number(raw.audienceNumber) : undefined,
        // API returns venueCapacity as number; schema expects the string label
        venueCapacity: raw.venueCapacity != null ? String(raw.venueCapacity) : undefined,
        eventName: raw.eventName ?? undefined,
        eventWebsiteURL: raw.eventWebsiteURL ?? undefined,
        otherActs: raw.otherActs ?? undefined,
        evidenceURL: raw.evidenceURL ?? undefined,
        lineUpPosition: raw.lineUpPosition ?? undefined,
        promotor: raw.promotor ?? undefined,
        promotorName: raw.promotorName ?? undefined,
        promotorEmail: raw.promotorEmail ?? undefined,
        promotorStreet: raw.promotorStreet ?? undefined,
        promotorCity: raw.promotorCity ?? undefined,
        promotorPostcode: raw.promotorPostcode ?? undefined,
        promotorCountry: raw.promotorCountry ?? undefined,
        promotorPhone: raw.promotorPhone ?? undefined,
        promotorAddress1: raw.promotorAddress1 ?? undefined,
        festivalStageName: raw.festivalStageName ?? undefined,
        festivalPosition: raw.festivalPosition ?? undefined,
        headlineArtist: raw.headlineArtist ?? undefined,
        supportArtist1: raw.supportArtist1 ?? undefined,
        supportArtist2: raw.supportArtist2 ?? undefined,
        supportArtist3: raw.supportArtist3 ?? undefined,
      };
      form.reset(values);
    }
  }, [gigClaim.data, id, form]);

  useEffect(() => {
    const def = FLAGS.find(f => f.id === "default")!;
    const spef = FLAGS.find(f => f.id === watchedCountry);
    setSections(spef ? spef.onSections : def.onSections);
    setFestival(false);

    if (watchedCountry === "US") {
      setIsUsAlertOpen(true);
    }
    if (watchedCountry === "DK") {
      setIsDenmarkAlertOpen(true);
    }

    // Clear venue-related fields when the user changes country, but skip the
    // very first time this runs (initial mount / form.reset() populating an edit).
    if (previousCountryRef.current !== undefined && previousCountryRef.current !== watchedCountry) {
      form.setValue("venueName", "");
      form.setValue("address", "");
      form.setValue("city", "");
      form.setValue("postcode", "");
      form.setValue("venueId", undefined);
      form.setValue("venuePremisesCategoryId", undefined);
      form.setValue("venuePremisesTypeId", undefined);
      form.setValue("prsId", undefined);
      form.setValue("venueIsApproved", undefined);
    }
    previousCountryRef.current = watchedCountry;
  }, [watchedCountry, form]);

  useEffect(() => {
    if (watchedCountry === "GB" && watchedVenueCategoryId === 5) {
      setFestival(true);
      setOtherPerformers(false);
    }
    else if (watchedCountry === "GB" && watchedVenueCategoryId === 2) {
      setOtherPerformers(true);
      setFestival(false);
    }
    else if (watchedCountry === "FR") {
      setOtherPerformers(true);
    }
    else {
      setFestival(false);
      setOtherPerformers(false);
    }
  }, [watchedVenueCategoryId, watchedCountry]);

  return {
    form,
    id,
    isUpdate,
    showManual,
    setShowManual,
    sections,
    festival,
    otherPerformers,
    underTwelveEuros,
    isUsAlertOpen,
    setIsUsAlertOpen,
    isDenmarkAlertOpen,
    setIsDenmarkAlertOpen,
    adminRights,
    myAccount,
    setlists,
    gigClaims,
    profile,
    gigClaim,
    refetchGigClaim,
  };
}
